import React, { useState, useEffect } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR, defaultMasterRates } from '../../data/products';
import { 
  TrendingUp, 
  TrendingDown,
  ShieldCheck, 
  Check, 
  Sparkles, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Zap, 
  AlertCircle,
  Coins,
  Save,
  Clock,
  Sliders,
  ArrowRight,
  Calculator,
  Tag,
  Radio,
  FileText,
  Percent,
  SlidersHorizontal
} from 'lucide-react';

export default function AdminGoldRates() {
  const { 
    masterMetalRates, 
    updateMasterMetalRates, 
    liveGoldRates, 
    rateApiSettings,
    updateRateApiSettings,
    marketingOfferData,
    updateMarketingOfferData,
    rateHistoryData,
    refreshLiveRates,
    addToast 
  } = useShop();

  // Active Admin Sub-tab
  const [activeTab, setActiveTab] = useState('api-settings'); // 'api-settings', 'manual-rates', 'promo-banner', 'history-log'

  // Local editable copy of master rates
  const [rates, setRates] = useState(() => {
    return masterMetalRates || defaultMasterRates;
  });

  const [rateChange, setRateChange] = useState(() => {
    return liveGoldRates?.change || liveGoldRates?.rateChange || '+0.42%';
  });

  // Local editable copy of API Settings
  const [localApiSettings, setLocalApiSettings] = useState(() => ({
    isLiveApiEnabled: rateApiSettings?.isLiveApiEnabled !== undefined ? rateApiSettings.isLiveApiEnabled : true,
    apiSource: rateApiSettings?.apiSource || 'ibja_benchmark',
    marginPerGram: rateApiSettings?.marginPerGram || 0,
    defaultMakingCharge: rateApiSettings?.defaultMakingCharge || 6.99,
    makingDiscountPercent: rateApiSettings?.makingDiscountPercent || 50
  }));

  // Local editable copy of Promo Banner
  const [localPromo, setLocalPromo] = useState(() => ({
    title: marketingOfferData?.title || "✨ TODAY'S SPECIAL JEWELLERY OFFER ✨",
    subtitle: marketingOfferData?.subtitle || "Gold Jewellery Starting at Special Making Charges",
    discountText: marketingOfferData?.discountText || "Get Up To 50% OFF on Making Charges",
    badge: marketingOfferData?.badge || "Limited Time Offer",
    shopUrl: marketingOfferData?.shopUrl || "/category/all",
    whatsappNumber: marketingOfferData?.whatsappNumber || "+919810023456",
    phoneNumber: marketingOfferData?.phoneNumber || "+919810023456",
    isEnabled: marketingOfferData?.isEnabled !== false
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Sync when context changes
  useEffect(() => {
    if (masterMetalRates) {
      setRates(masterMetalRates);
    }
    if (liveGoldRates?.change || liveGoldRates?.rateChange) {
      setRateChange(liveGoldRates?.change || liveGoldRates?.rateChange);
    }
    if (rateApiSettings) {
      setLocalApiSettings({
        isLiveApiEnabled: rateApiSettings.isLiveApiEnabled !== undefined ? rateApiSettings.isLiveApiEnabled : true,
        apiSource: rateApiSettings.apiSource || 'ibja_benchmark',
        marginPerGram: rateApiSettings.marginPerGram || 0,
        defaultMakingCharge: rateApiSettings.defaultMakingCharge || 6.99,
        makingDiscountPercent: rateApiSettings.makingDiscountPercent || 50
      });
    }
    if (marketingOfferData) {
      setLocalPromo({
        title: marketingOfferData.title || "✨ TODAY'S SPECIAL JEWELLERY OFFER ✨",
        subtitle: marketingOfferData.subtitle || "Gold Jewellery Starting at Special Making Charges",
        discountText: marketingOfferData.discountText || "Get Up To 50% OFF on Making Charges",
        badge: marketingOfferData.badge || "Limited Time Offer",
        shopUrl: marketingOfferData.shopUrl || "/category/all",
        whatsappNumber: marketingOfferData.whatsappNumber || "+919810023456",
        phoneNumber: marketingOfferData.phoneNumber || "+919810023456",
        isEnabled: marketingOfferData.isEnabled !== false
      });
    }
  }, [masterMetalRates, liveGoldRates, rateApiSettings, marketingOfferData]);

  // New Custom Gold Carat State
  const [newGoldLabel, setNewGoldLabel] = useState('');
  const [newGoldRate, setNewGoldRate] = useState('');

  // New Custom Silver Purity State
  const [newSilverLabel, setNewSilverLabel] = useState('');
  const [newSilverRate, setNewSilverRate] = useState('');

  // Rate Change Handlers
  const handleGoldGramRateChange = (id, newGramRate) => {
    const numericRate = newGramRate === '' ? '' : parseFloat(newGramRate);
    setRates((prev) => ({
      ...prev,
      gold: prev.gold.map((item) => (item.id === id ? { ...item, rate: numericRate } : item))
    }));
  };

  const handleGoldTenGramRateChange = (id, newTenGramRate) => {
    const numericRate = newTenGramRate === '' ? '' : parseFloat(newTenGramRate);
    const gramRate = numericRate === '' ? '' : parseFloat((numericRate / 10).toFixed(2));
    setRates((prev) => ({
      ...prev,
      gold: prev.gold.map((item) => (item.id === id ? { ...item, rate: gramRate } : item))
    }));
  };

  const handleSilverGramRateChange = (id, newGramRate) => {
    const numericRate = newGramRate === '' ? '' : parseFloat(newGramRate);
    setRates((prev) => ({
      ...prev,
      silver: prev.silver.map((item) => (item.id === id ? { ...item, rate: numericRate } : item))
    }));
  };

  const handleSilverKgRateChange = (id, newKgRate) => {
    const numericRate = newKgRate === '' ? '' : parseFloat(newKgRate);
    const gramRate = numericRate === '' ? '' : parseFloat((numericRate / 1000).toFixed(2));
    setRates((prev) => ({
      ...prev,
      silver: prev.silver.map((item) => (item.id === id ? { ...item, rate: gramRate } : item))
    }));
  };

  // Auto-calculate 22K, 18K, 14K from 24K rate
  const handleAutoDeriveCarats = () => {
    const rate24KItem = rates.gold.find((g) => g.label?.includes('24') || g.id?.includes('24'));
    const rate24K = parseFloat(rate24KItem?.rate) || 7400;

    const rate22K = Math.round((rate24K * 22) / 24);
    const rate18K = Math.round((rate24K * 18) / 24);
    const rate14K = Math.round((rate24K * 14) / 24);

    setRates((prev) => ({
      ...prev,
      gold: prev.gold.map((item) => {
        const idLower = (item.id || '').toLowerCase();
        const lblLower = (item.label || '').toLowerCase();
        if (idLower.includes('22') || lblLower.includes('22')) return { ...item, rate: rate22K };
        if (idLower.includes('18') || lblLower.includes('18')) return { ...item, rate: rate18K };
        if (idLower.includes('14') || lblLower.includes('14')) return { ...item, rate: rate14K };
        return item;
      })
    }));

    addToast(`Auto-calculated 22K (₹${rate22K}), 18K (₹${rate18K}), and 14K (₹${rate14K}) based on 24K!`, 'info');
  };

  // Add Custom Gold Carat
  const handleAddGoldCarat = (e) => {
    e.preventDefault();
    if (!newGoldLabel.trim() || !newGoldRate) {
      addToast('Please enter both Carat Label and Rate per gram', 'error');
      return;
    }
    const slug = newGoldLabel.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    let cleanRate = parseFloat(newGoldRate);
    if (cleanRate > 20000) cleanRate = Math.round(cleanRate / 10);

    const newItem = {
      id: slug || `custom-${Date.now()}`,
      label: newGoldLabel.trim(),
      rate: cleanRate,
      isDefault: false
    };

    setRates((prev) => ({
      ...prev,
      gold: [...prev.gold, newItem]
    }));
    setNewGoldLabel('');
    setNewGoldRate('');
    addToast(`Added ${newItem.label} Gold to staging list.`, 'success');
  };

  // Add Custom Silver Purity
  const handleAddSilverPurity = (e) => {
    e.preventDefault();
    if (!newSilverLabel.trim() || !newSilverRate) {
      addToast('Please enter both Purity Label and Rate per gram', 'error');
      return;
    }
    const slug = newSilverLabel.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    let cleanRate = parseFloat(newSilverRate);
    if (cleanRate > 1000) cleanRate = parseFloat((cleanRate / 1000).toFixed(2));

    const newItem = {
      id: slug || `custom-sil-${Date.now()}`,
      label: newSilverLabel.trim(),
      rate: cleanRate,
      isDefault: false
    };

    setRates((prev) => ({
      ...prev,
      silver: [...prev.silver, newItem]
    }));
    setNewSilverLabel('');
    setNewSilverRate('');
    addToast(`Added ${newItem.label} to staging list.`, 'success');
  };

  const handleGoldLabelChange = (id, newLabel) => {
    setRates((prev) => ({
      ...prev,
      gold: prev.gold.map((item) => (item.id === id ? { ...item, label: newLabel } : item))
    }));
  };

  const handleSilverLabelChange = (id, newLabel) => {
    setRates((prev) => ({
      ...prev,
      silver: prev.silver.map((item) => (item.id === id ? { ...item, label: newLabel } : item))
    }));
  };

  const handleDeleteGoldCarat = (id) => {
    if (rates.gold.length <= 1) {
      addToast('At least one gold rate must remain configured.', 'warning');
      return;
    }
    setRates((prev) => ({
      ...prev,
      gold: prev.gold.filter((g) => g.id !== id)
    }));
    addToast('Gold carat removed.', 'info');
  };

  const handleDeleteSilverPurity = (id) => {
    if (rates.silver.length <= 1) {
      addToast('At least one silver rate must remain configured.', 'warning');
      return;
    }
    setRates((prev) => ({
      ...prev,
      silver: prev.silver.filter((s) => s.id !== id)
    }));
    addToast('Silver purity rate removed.', 'info');
  };

  // Save All Settings & Manual Rates
  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      // 1. Save API settings
      await updateRateApiSettings(localApiSettings);

      // 2. Save Promo Banner
      await updateMarketingOfferData(localPromo);

      // 3. Save Master Rates
      await updateMasterMetalRates(rates);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      addToast('All Bullion rates, API settings & Promotional banner broadcasted live!', 'success');
    } catch (err) {
      addToast('Failed to save settings: ' + err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-rates-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      
      {/* Top Header Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: '4px',
        padding: '1.2rem 1.6rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '1rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#2874f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff'
            }}>
              <Coins size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#212121' }}>
                Live Gold & Silver Rate Control Center
              </h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#666' }}>
                Configure live Indian bullion API feeds, set showroom margin premiums, override rates, and manage promotional offers.
              </p>
            </div>
          </div>
        </div>

        {/* Global Save Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button
            onClick={() => refreshLiveRates(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: '2px',
              padding: '0.55rem 0.95rem',
              fontSize: '0.82rem',
              fontWeight: 700,
              color: '#334155',
              cursor: 'pointer'
            }}
          >
            <RefreshCw size={14} />
            <span>Poll Live API</span>
          </button>

          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              background: saveSuccess ? '#16a34a' : '#2874f0',
              border: 'none',
              borderRadius: '2px',
              padding: '0.55rem 1.4rem',
              fontSize: '0.84rem',
              fontWeight: 700,
              color: '#ffffff',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(40, 116, 240, 0.25)'
            }}
          >
            {saveSuccess ? <Check size={16} /> : <Save size={16} />}
            <span>{isSaving ? 'Broadcasting...' : saveSuccess ? 'Saved Live!' : 'Save & Broadcast'}</span>
          </button>
        </div>
      </div>

      {/* Admin Navigation Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.4rem',
        marginBottom: '1rem',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '0.4rem'
      }}>
        {[
          { id: 'api-settings', label: '1. Live Bullion API & Margins', icon: <Zap size={15} /> },
          { id: 'manual-rates', label: '2. Rate Overrides & Carats', icon: <Sliders size={15} /> },
          { id: 'promo-banner', label: '3. Special Offer Banner CMS', icon: <Tag size={15} /> },
          { id: 'history-log', label: '4. Rate History & Trend Log', icon: <Clock size={15} /> },
        ].map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.45rem',
                padding: '0.55rem 1rem',
                borderRadius: '4px 4px 0 0',
                border: 'none',
                background: isActive ? '#2874f0' : '#ffffff',
                color: isActive ? '#ffffff' : '#475569',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: isActive ? '0 -1px 3px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* SUB-TAB 1: LIVE API & SHOWROOM MARGIN CONTROLS */}
      {/* ========================================================================= */}
      {activeTab === 'api-settings' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '4px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1.2rem', fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
            Live Bullion API Source & Selling Rate Configuration
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem' }}>
            
            {/* Live API Enable / Disable Switch */}
            <div style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>
                Live API Rate Feed Engine:
              </label>
              <div style={{ display: 'flex', gap: '0.8rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  <input
                    type="radio"
                    name="isLiveApiEnabled"
                    checked={localApiSettings.isLiveApiEnabled === true}
                    onChange={() => setLocalApiSettings((p) => ({ ...p, isLiveApiEnabled: true }))}
                  />
                  <span style={{ color: '#16a34a' }}>● Enabled (Auto-sync with Live Market)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  <input
                    type="radio"
                    name="isLiveApiEnabled"
                    checked={localApiSettings.isLiveApiEnabled === false}
                    onChange={() => setLocalApiSettings((p) => ({ ...p, isLiveApiEnabled: false }))}
                  />
                  <span style={{ color: '#dc2626' }}>Manual Override Mode Only</span>
                </label>
              </div>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: '#64748b' }}>
                When enabled, rates auto-refresh every 60 seconds from Indian bullion benchmark feeds without needing manual edits.
              </p>
            </div>

            {/* Bullion API Source Selector */}
            <div style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                Select Gold & Silver API Source:
              </label>
              <select
                value={localApiSettings.apiSource}
                onChange={(e) => setLocalApiSettings((p) => ({ ...p, apiSource: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                  color: '#0f172a'
                }}
              >
                <option value="ibja_benchmark">IBJA Benchmark (India Bullion & Jewellers Association)</option>
                <option value="mcx_bullion">MCX Indian Spot Bullion Index Feed</option>
                <option value="goldapi_io">GoldAPI.io Indian Market Spot Feed</option>
                <option value="manual">Manual Client Master Feed</option>
              </select>
              <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <ShieldCheck size={12} />
                <span>API credentials stored securely on backend. Never exposed to frontend JS.</span>
              </div>
            </div>

            {/* Showroom Per-Gram Premium / Margin */}
            <div style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                Showroom Per-Gram Margin/Premium (₹/g):
              </label>
              <input
                type="number"
                step="5"
                min="0"
                value={localApiSettings.marginPerGram}
                onChange={(e) => setLocalApiSettings((p) => ({ ...p, marginPerGram: parseFloat(e.target.value) || 0 }))}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g. 50 (₹50/g markup)"
              />
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.72rem', color: '#64748b' }}>
                Adds this markup directly to the live base bullion rate across the showroom catalog.
              </p>
            </div>

            {/* Standard Showroom Making Charges */}
            <div style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                Default Showroom Making Charge (%):
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={localApiSettings.defaultMakingCharge}
                onChange={(e) => setLocalApiSettings((p) => ({ ...p, defaultMakingCharge: parseFloat(e.target.value) || 0 }))}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g. 6.99"
              />
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.72rem', color: '#64748b' }}>
                Applied by default when individual products do not have custom making charges specified.
              </p>
            </div>

            {/* Showroom Making Charge Offer Discount */}
            <div style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                Making Charge Offer Discount (% OFF):
              </label>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={localApiSettings.makingDiscountPercent}
                onChange={(e) => setLocalApiSettings((p) => ({ ...p, makingDiscountPercent: parseFloat(e.target.value) || 0 }))}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g. 50"
              />
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.72rem', color: '#64748b' }}>
                Default discount % deducted from making charges in the calculator and offer banners.
              </p>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 2: MANUAL RATE OVERRIDES & CARAT CUSTOMIZATION */}
      {/* ========================================================================= */}
      {activeTab === 'manual-rates' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '4px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.8rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                Gold & Silver Manual Carat Overrides
              </h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.76rem', color: '#666' }}>
                Edit rates per gram or per 10 grams. Changes instantly recalculate every dynamic product price in the store.
              </p>
            </div>

            <button
              onClick={handleAutoDeriveCarats}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: '#fff8e1',
                border: '1px solid #ffe082',
                color: '#b78103',
                fontSize: '0.78rem',
                fontWeight: 700,
                padding: '0.45rem 0.85rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <Zap size={14} />
              <span>Auto-Derive 22K/18K/14K from 24K</span>
            </button>
          </div>

          {/* Gold Carats Table */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', fontWeight: 800, color: '#b45309', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Coins size={16} />
              <span>Gold Carat Rates</span>
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #cbd5e1', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Carat / Purity Label</th>
                    <th style={{ padding: '8px 12px' }}>Rate per Gram (₹)</th>
                    <th style={{ padding: '8px 12px' }}>Rate per 10 Grams (₹)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.gold.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>
                        <input
                          type="text"
                          value={item.label || ''}
                          onChange={(e) => handleGoldLabelChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '140px',
                            color: '#1e293b'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number"
                          value={item.rate || ''}
                          onChange={(e) => handleGoldGramRateChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '110px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number"
                          value={item.rate ? Math.round(item.rate * 10) : ''}
                          onChange={(e) => handleGoldTenGramRateChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '120px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {rates.gold.length > 1 && (
                          <button
                            type="button"
                            title="Delete Carat"
                            onClick={() => handleDeleteGoldCarat(item.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Quick Add Custom Gold Carat Form */}
            <form onSubmit={handleAddGoldCarat} style={{
              marginTop: '0.8rem',
              padding: '0.8rem',
              backgroundColor: '#fffbeb',
              borderRadius: '6px',
              border: '1px dashed #f59e0b',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.6rem',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#b45309', minWidth: '130px' }}>
                + Add Gold Carat:
              </span>
              <input
                type="text"
                placeholder="Carat Name (e.g. 20KT, 10KT)"
                value={newGoldLabel}
                onChange={(e) => setNewGoldLabel(e.target.value)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  flex: '1 1 140px'
                }}
              />
              <input
                type="number"
                placeholder="Rate / Gram (₹)"
                value={newGoldRate}
                onChange={(e) => setNewGoldRate(e.target.value)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  width: '130px'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '5px 12px',
                  backgroundColor: '#b45309',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Add Gold
              </button>
            </form>
          </div>

          {/* Silver Purity Table */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 800, color: '#1e40af', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Coins size={16} />
                <span>Silver Purity & Custom Rates (Any Purity / Chandi Rate)</span>
              </h4>
              <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                💡 Not restricted to 925 — edit labels, rates or add 999, 800, 70% silver freely
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #cbd5e1', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Purity Label (Editable)</th>
                    <th style={{ padding: '8px 12px' }}>Rate per Gram (₹)</th>
                    <th style={{ padding: '8px 12px' }}>Rate per Kilogram (₹)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.silver.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>
                        <input
                          type="text"
                          value={item.label || ''}
                          onChange={(e) => handleSilverLabelChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '160px',
                            color: '#1e293b'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number"
                          step="0.1"
                          value={item.rate || ''}
                          onChange={(e) => handleSilverGramRateChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '110px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number"
                          value={item.rate ? Math.round(item.rate * 1000) : ''}
                          onChange={(e) => handleSilverKgRateChange(item.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontWeight: 700,
                            width: '120px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {rates.silver.length > 1 && (
                          <button
                            type="button"
                            title="Delete Silver Purity (e.g. remove 925)"
                            onClick={() => handleDeleteSilverPurity(item.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Form to Add Custom Silver Purity & Rate */}
            <form onSubmit={handleAddSilverPurity} style={{
              marginTop: '0.8rem',
              padding: '0.8rem',
              backgroundColor: '#eff6ff',
              borderRadius: '6px',
              border: '1px dashed #3b82f6',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.6rem',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e40af', minWidth: '150px' }}>
                + Add Custom Silver Rate:
              </span>
              <input
                type="text"
                placeholder="Purity Label (e.g. 800 Silver, 70% Chandi, Fine 999)"
                value={newSilverLabel}
                onChange={(e) => setNewSilverLabel(e.target.value)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  flex: '1 1 180px'
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Rate / Gram (₹)"
                value={newSilverRate}
                onChange={(e) => setNewSilverRate(e.target.value)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  width: '130px'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '5px 14px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Add Silver Purity
              </button>

              {/* Quick Preset Buttons */}
              <div style={{ width: '100%', display: 'flex', gap: '0.4rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', color: '#64748b', alignSelf: 'center' }}>Presets:</span>
                <button
                  type="button"
                  onClick={() => { setNewSilverLabel('800 Traditional Silver'); setNewSilverRate('71.6'); }}
                  style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '3px', border: '1px solid #93c5fd', background: '#ffffff', cursor: 'pointer', color: '#1d4ed8' }}
                >
                  + 800 Traditional Silver (₹71.60/g)
                </button>
                <button
                  type="button"
                  onClick={() => { setNewSilverLabel('70% Purity Chandi'); setNewSilverRate('62.6'); }}
                  style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '3px', border: '1px solid #93c5fd', background: '#ffffff', cursor: 'pointer', color: '#1d4ed8' }}
                >
                  + 70% Purity Chandi (₹62.60/g)
                </button>
                <button
                  type="button"
                  onClick={() => { setNewSilverLabel('999 Fine Silver'); setNewSilverRate('89.5'); }}
                  style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '3px', border: '1px solid #93c5fd', background: '#ffffff', cursor: 'pointer', color: '#1d4ed8' }}
                >
                  + 999 Fine Silver (₹89.50/g)
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 3: PROMOTIONAL OFFER BANNER CMS */}
      {/* ========================================================================= */}
      {activeTab === 'promo-banner' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '4px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1.2rem', fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
            Marketing Offer Banner (Appears Immediately Above Live Bullion Rates)
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                Banner Offer Title / Headline:
              </label>
              <input
                type="text"
                value={localPromo.title}
                onChange={(e) => setLocalPromo((p) => ({ ...p, title: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                Subtitle / Description:
              </label>
              <input
                type="text"
                value={localPromo.subtitle}
                onChange={(e) => setLocalPromo((p) => ({ ...p, subtitle: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                Discount Highlight Text:
              </label>
              <input
                type="text"
                value={localPromo.discountText}
                onChange={(e) => setLocalPromo((p) => ({ ...p, discountText: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                Badge Tag Text:
              </label>
              <input
                type="text"
                value={localPromo.badge}
                onChange={(e) => setLocalPromo((p) => ({ ...p, badge: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                WhatsApp Inquiries Number:
              </label>
              <input
                type="text"
                value={localPromo.whatsappNumber}
                onChange={(e) => setLocalPromo((p) => ({ ...p, whatsappNumber: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.35rem' }}>
                Call Now Contact Number:
              </label>
              <input
                type="text"
                value={localPromo.phoneNumber}
                onChange={(e) => setLocalPromo((p) => ({ ...p, phoneNumber: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <input
              type="checkbox"
              id="enablePromoBanner"
              checked={localPromo.isEnabled}
              onChange={(e) => setLocalPromo((p) => ({ ...p, isEnabled: e.target.checked }))}
            />
            <label htmlFor="enablePromoBanner" style={{ fontSize: '0.84rem', fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>
              Display promotional banner actively above live rates on homepage
            </label>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 4: RATE HISTORY & AUDIT LOG */}
      {/* ========================================================================= */}
      {activeTab === 'history-log' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '4px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
            Bullion Rate Audit & Historical Record
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #cbd5e1', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Period / Date</th>
                  <th style={{ padding: '8px 12px' }}>24K Gold (₹/g)</th>
                  <th style={{ padding: '8px 12px' }}>22K Gold (₹/g)</th>
                  <th style={{ padding: '8px 12px' }}>18K Gold (₹/g)</th>
                  <th style={{ padding: '8px 12px' }}>999 Silver (₹/g)</th>
                  <th style={{ padding: '8px 12px' }}>Benchmark Source</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { date: 'Today (Live)', g24: liveGoldRates?.gold24K || 7380, g22: liveGoldRates?.gold22K || 6765, g18: liveGoldRates?.gold18K || 5535, s: liveGoldRates?.silver999 || 89.5, source: 'IBJA Spot API' },
                  { date: 'Yesterday', g24: (liveGoldRates?.gold24K || 7380) - 8, g22: (liveGoldRates?.gold22K || 6765) - 7, g18: (liveGoldRates?.gold18K || 5535) - 6, s: (liveGoldRates?.silver999 || 89.5) - 0.1, source: 'IBJA Benchmark' },
                  { date: '2 Days Ago', g24: (liveGoldRates?.gold24K || 7380) + 10, g22: (liveGoldRates?.gold22K || 6765) + 9, g18: (liveGoldRates?.gold18K || 5535) + 7, s: (liveGoldRates?.silver999 || 89.5) + 0.2, source: 'IBJA Benchmark' },
                  { date: '1 Week Ago', g24: (liveGoldRates?.gold24K || 7380) - 45, g22: (liveGoldRates?.gold22K || 6765) - 41, g18: (liveGoldRates?.gold18K || 5535) - 34, s: (liveGoldRates?.silver999 || 89.5) - 1.2, source: 'IBJA Benchmark' },
                  { date: '1 Month Ago', g24: (liveGoldRates?.gold24K || 7380) - 110, g22: (liveGoldRates?.gold22K || 6765) - 100, g18: (liveGoldRates?.gold18K || 5535) - 82, s: (liveGoldRates?.silver999 || 89.5) - 2.5, source: 'IBJA Benchmark' },
                ].map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>{row.date}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{formatINR(row.g24)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{formatINR(row.g22)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{formatINR(row.g18)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>₹{row.s}</td>
                    <td style={{ padding: '8px 12px', color: '#2563eb' }}>{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
